import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Robot, Brain, Sparkle, X, ArrowRight, Check } from '@phosphor-icons/react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const SECTORS = [
  { code: 'Tech',     label: 'Tech',     emoji: '💻', blurb: 'AI, software, semis, internet' },
  { code: 'Finance',  label: 'Finance',  emoji: '🏦', blurb: 'Banks, payments, insurance' },
  { code: 'Energy',   label: 'Energy',   emoji: '⚡', blurb: 'Oil & gas, solar, EV power' },
  { code: 'Consumer', label: 'Consumer', emoji: '🛒', blurb: 'Retail, food, brands' },
  { code: 'All',      label: 'Surprise me', emoji: '🎲', blurb: 'Diverse picks across sectors' },
];

const AIAnalystActivationModal = ({ open, onClose, onActivated }) => {
  const [step, setStep] = useState(0); // 0 = sectors, 1 = reasoning
  const [selected, setSelected] = useState([]);
  const [reasoning, setReasoning] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep(0);
    setSelected([]);
    setReasoning('');
    setSubmitting(false);
  };

  const toggle = (code) => {
    setSelected((prev) => {
      if (code === 'All') return prev.includes('All') ? [] : ['All'];
      const next = prev.filter((s) => s !== 'All');
      return next.includes(code) ? next.filter((s) => s !== code) : [...next, code];
    });
  };

  const handleNext = () => {
    if (!selected.length) {
      toast.error('Pick at least one sector!');
      return;
    }
    setStep(1);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/ai-analyst/activate', { sectors: selected, reasoning: reasoning.trim() });
      toast.success('AI Analyst is on the case 🤖');
      onActivated?.(res.data.analysis);
      reset();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not activate AI Analyst';
      toast.error(typeof msg === 'string' ? msg : 'Could not activate AI Analyst');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        data-testid="ai-analyst-activation-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.96 }}
          className="w-full max-w-xl bg-zinc-950 border border-cyan-400/30 rounded-3xl shadow-2xl shadow-cyan-500/10 p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Brain size={24} weight="fill" className="text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-unbounded font-bold text-white">Activate AI Analyst</h2>
                <p className="text-xs text-zinc-400">{step === 0 ? 'Step 1 of 2 — Pick your interests' : 'Step 2 of 2 — Tell us why'}</p>
              </div>
            </div>
            <button
              onClick={() => { reset(); onClose?.(); }}
              className="text-zinc-500 hover:text-white transition-colors"
              data-testid="ai-analyst-modal-close"
            >
              <X size={22} />
            </button>
          </div>

          {/* Step indicator */}
          <div className="flex gap-1.5 mb-6">
            {[0, 1].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  step >= i ? 'bg-gradient-to-r from-cyan-400 to-blue-500' : 'bg-white/10'
                }`}
              />
            ))}
          </div>

          {step === 0 ? (
            <>
              <p className="text-zinc-300 mb-5 text-sm leading-relaxed">
                Which sectors do you want the analyst to focus on? Pick one or several — or just tap <span className="text-cyan-300 font-semibold">Surprise me</span>.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
                {SECTORS.map((s) => {
                  const active = selected.includes(s.code);
                  return (
                    <button
                      key={s.code}
                      onClick={() => toggle(s.code)}
                      className={`text-left px-4 py-3 rounded-xl border-2 transition-all ${
                        active
                          ? 'border-cyan-400 bg-cyan-400/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'
                      }`}
                      data-testid={`ai-analyst-sector-${s.code}`}
                      type="button"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{s.emoji}</span>
                          <span className="font-semibold text-white">{s.label}</span>
                        </div>
                        {active && (
                          <div className="w-5 h-5 rounded-full bg-cyan-400 flex items-center justify-center">
                            <Check size={12} weight="bold" className="text-black" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-zinc-400">{s.blurb}</p>
                    </button>
                  );
                })}
              </div>
              <Button
                onClick={handleNext}
                disabled={!selected.length}
                className="w-full py-6 bg-gradient-to-r from-cyan-400 to-blue-500 text-black font-bold rounded-full disabled:opacity-40"
                data-testid="ai-analyst-step-next"
              >
                Next <ArrowRight size={18} weight="bold" className="ml-1" />
              </Button>
            </>
          ) : (
            <>
              <p className="text-zinc-300 mb-3 text-sm leading-relaxed">
                In a sentence or two, <span className="text-cyan-300 font-semibold">why</span> do you care about{' '}
                {selected.includes('All') ? 'a diverse mix' : selected.join(' + ')}?{' '}
                <span className="text-zinc-500">(optional but it makes picks more personal)</span>
              </p>
              <Textarea
                value={reasoning}
                onChange={(e) => setReasoning(e.target.value)}
                placeholder="e.g. I'm fascinated by how AI is changing software, and I think solar is winning long-term."
                rows={5}
                maxLength={1000}
                className="bg-black/40 border-white/10 mb-2 min-h-[120px]"
                data-testid="ai-analyst-reasoning-input"
              />
              <p className="text-xs text-zinc-500 mb-5">{reasoning.length}/1000 — leave blank if you want.</p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setStep(0)}
                  className="flex-1 border border-white/10"
                  data-testid="ai-analyst-step-back"
                >
                  ← Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-[2] py-6 bg-gradient-to-r from-cyan-400 to-blue-500 text-black font-bold rounded-full"
                  data-testid="ai-analyst-activate-submit"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <Sparkle size={18} weight="fill" className="animate-pulse" /> Analyzing portfolio...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Robot size={18} weight="fill" /> Activate AI Analyst
                    </span>
                  )}
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AIAnalystActivationModal;
